import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import {
    FormBuilder,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DropdownChangeEvent } from 'primeng/dropdown';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule, TablePageEvent } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { languages } from 'src/app/demo/constants/languages';
import { Customer } from 'src/app/demo/models/customer';
import { CustomersService } from 'src/app/demo/service/customers.service';
import { LanguageService } from 'src/app/demo/service/language.service';
import { ProgressLoaderService } from 'src/app/demo/service/progress-loader.service';
import { showErrorToast } from 'src/app/demo/utilities/toast-utils';
import { NgxTranslateModule } from 'src/app/translate.module';

@Component({
    selector: 'app-search-customer',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        ToastModule,
        ButtonModule,
        TableModule,
        InputTextModule,
        NgxTranslateModule,
        InputGroupModule,
        TooltipModule,
    ],
    templateUrl: './search-customer.component.html',
    styleUrl: './search-customer.component.scss',
    providers:[MessageService]
})
export class SearchCustomerComponent {
    searchForm!: FormGroup;
    customerSearchTerm: string = '';
    currentPage: number = 0;
    pageSize: number = 10;
    sort!: string[];
    customers!: Customer[];
    languages: any[] = languages;
    selectedLanguage!: string;

    ngOnInit() {
        this.initializeForm();
        this.initializeLocale();
    }

    initializeLocale() {
        this.languageService.currentLanguage$.subscribe((language) => {
            this.selectedLanguage = language;
            this.setLanguage(this.selectedLanguage);
        });
    }

    setLanguage(language: string) {
        this.translateService.use(language);
        this.languageService.setLanguage(language);
    }

    languageChanged(event: DropdownChangeEvent) {
        this.setLanguage(event.value);
    }

    initializeForm() {
        this.searchForm = this.fb.group({
            searchTermInput: ['', Validators.required],
        });
        this.searchCustomerClicked();
    }

    logSearchTerm() {
        this.searchCustomerClicked();
    }

    searchCustomerClicked() {
        this.customerSearchTerm =
            this.searchForm.getRawValue()['searchTermInput'];

        this.customerSearchTerm.length >= 3 ? this.searchCustomer(this.customerSearchTerm) : this.showError(this.translateService.instant('SEARCH_TERM_MIN_LENGTH'));
    }

    viewCustomerClicked(customer: Customer) {
        this.router.navigate(['/home/customers/' + customer.id]);
    }

    searchCustomer(searchTerm: string) {
        this.progressLoader.show();
        this.customerService
            .searchCustomer(this.getSearchCustomerPayload(searchTerm))
            .subscribe({
                next: (value) => {
                    this.progressLoader.hide();
                    this.customers = value['content'];
                    
                    if(this.customers.length===0){
                        this.showWarning(this.translateService.instant('CUSTOMER_NOT_FOUND'));   
                    }
                    this.progressLoaderService.hide();
                },
                error: (err) => {
                    this.progressLoader.hide();
                    showErrorToast(this.translateService.instant(err.error.messageKey), this.messageService,this.translateService)
                },
            });
    }

    pageChange(event: TablePageEvent) { }

    getSearchCustomerPayload(searchTerm: string): any {
        return {
            searchTerm: searchTerm,
            pageable: {
                page: this.currentPage,
                size: this.pageSize,
                sort: [],
            },
        };
    }

    showSuccess(message: string){
        this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('Success'),  
            detail: message,
            life: 2000
        })
    }

    showError(message: string){
        this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('Error'),
            detail: message,
            life: 2000
        })
    }

    showWarning(message: string){
        this.messageService.add({
            severity: 'warn',
            summary: this.translateService.instant('Info'),
            detail: message,
            life: 2000
        })
    }

    constructor(
        private fb: FormBuilder,
        private customerService: CustomersService,
        private progressLoader: ProgressLoaderService,
        private router: Router,
        private messageService: MessageService,
        private progressLoaderService: ProgressLoaderService,
        private translateService: TranslateService,
        private languageService: LanguageService
    ) { }
}
